/**
 * Organization Context Hook
 * 
 * Uses Clerk Organizations to determine dashboard access and features.
 * Organizations are configured in Clerk dashboard with metadata for dashboard type.
 */

import { useOrganization, useOrganizationList, useUser } from '@clerk/clerk-react';
import { useMemo } from 'react';
import type { DashboardType, OrganizationConfig } from '@shared/organizations';

export interface OrganizationContext {
  /** Whether organization data is still loading */
  isLoading: boolean;
  /** Whether user has an active organization */
  hasOrganization: boolean;
  /** The organization ID */
  organizationId: string | null;
  /** The organization name */
  organizationName: string | null;
  /** The type of dashboard this organization should see */
  dashboardType: DashboardType;
  /** Feature flags for this organization */
  features: OrganizationConfig['features'];
  /** Whether this is an admin organization with full access */
  isAdmin: boolean;
  /** Whether this org only has access to the Loan Servicing tab */
  loanServicingOnly: boolean;
}

// Organization slug prefixes to dashboard type mapping
// Uses startsWith matching to handle Clerk's auto-generated slug suffixes
// NOTE: More specific prefixes MUST come before less specific ones (e.g., 'dignifi---loan-servicing' before 'dignifi')
const ORGANIZATION_DASHBOARD_PREFIXES: Array<{ prefix: string; dashboardType: DashboardType }> = [
  { prefix: 'dignifi---loan-servicing', dashboardType: 'welcome_agent' },
  { prefix: 'dignifi', dashboardType: 'welcome_agent' },
  { prefix: 'nationwide-capital', dashboardType: 'settlement' },
  { prefix: 'structured-settlement', dashboardType: 'settlement' },
  { prefix: 'unifipro', dashboardType: 'settlement' }, // Admin org defaults to settlement
];

// Helper function to match organization slug to dashboard type
function getDashboardTypeFromSlug(slug: string): DashboardType {
  const normalizedSlug = slug.toLowerCase();
  for (const { prefix, dashboardType } of ORGANIZATION_DASHBOARD_PREFIXES) {
    if (normalizedSlug.startsWith(prefix)) {
      return dashboardType;
    }
  }
  return 'settlement'; // Default
}

// Organization slug prefixes to feature flags mapping
// NOTE: More specific prefixes MUST come before less specific ones
const ORGANIZATION_FEATURES_PREFIXES: Array<{ prefix: string; features: OrganizationConfig['features'] }> = [
  {
    prefix: 'dignifi---loan-servicing',
    features: {
      settlements: false,
      welcomeAgent: true,
      agentMetrics: false,
      exports: false,
      callRecordings: false,
    },
  },
  {
    prefix: 'dignifi',
    features: {
      settlements: false,
      welcomeAgent: true,
      agentMetrics: true,
      exports: true,
      callRecordings: true,
    },
  },
  {
    prefix: 'nationwide-capital',
    features: {
      settlements: true,
      welcomeAgent: false,
      agentMetrics: true,
      exports: true,
      callRecordings: true,
    },
  },
  {
    prefix: 'structured-settlement',
    features: {
      settlements: true,
      welcomeAgent: false,
      agentMetrics: true,
      exports: true,
      callRecordings: true,
    },
  },
  {
    prefix: 'unifipro',
    features: {
      // Admin org has access to everything
      settlements: true,
      welcomeAgent: true,
      agentMetrics: true,
      exports: true,
      callRecordings: true,
    },
  },
];

// Helper function to get features from organization slug
function getFeaturesFromSlug(slug: string): OrganizationConfig['features'] {
  const normalizedSlug = slug.toLowerCase();
  for (const { prefix, features } of ORGANIZATION_FEATURES_PREFIXES) {
    if (normalizedSlug.startsWith(prefix)) {
      return features;
    }
  }
  return DEFAULT_FEATURES;
}

// Helper function to check if slug is admin org
function isAdminOrg(slug: string): boolean {
  return slug.toLowerCase().startsWith('unifipro');
}

// Helper function to check if slug is loan-servicing-only org
function isLoanServicingOnlyOrg(slug: string): boolean {
  return slug.toLowerCase().startsWith('dignifi---loan-servicing');
}

// Default features for unknown organizations
const DEFAULT_FEATURES: OrganizationConfig['features'] = {
  settlements: true,
  welcomeAgent: false,
  agentMetrics: true,
  exports: true,
  callRecordings: true,
};

/**
 * Hook to get the current user's organization context
 * 
 * This is the primary way to determine what a user can see and access.
 * All components should use this to conditionally render based on organization.
 */
export function useOrganizationContext(): OrganizationContext {
  const { isLoaded: userLoaded, user } = useUser();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { userMemberships, isLoaded: membershipsLoaded } = useOrganizationList({
    userMemberships: { infinite: true },
  });

  const context = useMemo((): OrganizationContext => {
    // Still loading
    if (!userLoaded || !orgLoaded || !membershipsLoaded) {
      return {
        isLoading: true,
        hasOrganization: false,
        organizationId: null,
        organizationName: null,
        dashboardType: 'settlement',
        features: {
          settlements: false,
          welcomeAgent: false,
          agentMetrics: false,
          exports: false,
          callRecordings: false,
        },
        isAdmin: false,
        loanServicingOnly: false,
      };
    }

    // No user - not authenticated
    if (!user) {
      return {
        isLoading: false,
        hasOrganization: false,
        organizationId: null,
        organizationName: null,
        dashboardType: 'settlement',
        features: {
          settlements: false,
          welcomeAgent: false,
          agentMetrics: false,
          exports: false,
          callRecordings: false,
        },
        isAdmin: false,
        loanServicingOnly: false,
      };
    }

    // Check if user has any organization memberships
    const memberships = userMemberships?.data || [];
    const hasOrg = memberships.length > 0 || !!organization;

    // If no organization, allow access with default features
    // This handles users who haven't been added to an org yet
    if (!hasOrg && !organization) {
      // For users without an org, give them default settlement access
      // They can still use the system, just with default features
      return {
        isLoading: false,
        hasOrganization: true, // Allow access
        organizationId: null,
        organizationName: 'Default',
        dashboardType: 'settlement',
        features: DEFAULT_FEATURES,
        isAdmin: false,
        loanServicingOnly: false,
      };
    }

    // Get the active organization (or first membership if no active org)
    const activeOrg = organization || memberships[0]?.organization;
    const orgSlug = activeOrg?.slug?.toLowerCase() || '';
    const orgName = activeOrg?.name || 'Organization';
    const orgId = activeOrg?.id || null;

    // Determine dashboard type from organization slug (uses prefix matching)
    const dashboardType = getDashboardTypeFromSlug(orgSlug);
    
    // Get features for this organization (uses prefix matching)
    const features = getFeaturesFromSlug(orgSlug);
    
    // Check if this is an admin organization
    const isAdmin = isAdminOrg(orgSlug);
    
    // Check if this is a loan-servicing-only organization
    const loanServicingOnly = isLoanServicingOnlyOrg(orgSlug);

    return {
      isLoading: false,
      hasOrganization: true,
      organizationId: orgId,
      organizationName: orgName,
      dashboardType,
      features,
      isAdmin,
      loanServicingOnly,
    };
  }, [user, userLoaded, organization, orgLoaded, userMemberships, membershipsLoaded]);

  return context;
}

/**
 * Hook to check if current organization has access to a specific feature
 */
export function useFeatureAccess(feature: keyof OrganizationConfig['features']): boolean {
  const { features, isLoading } = useOrganizationContext();
  
  if (isLoading) return false;
  return features[feature] === true;
}

/**
 * Hook to get navigation items for the current organization's dashboard type
 */
export function useNavigationItems() {
  const { dashboardType, isLoading } = useOrganizationContext();

  return useMemo(() => {
    if (isLoading) return [];

    if (dashboardType === 'settlement') {
      return [
        { icon: 'LayoutDashboard', label: 'Dashboard', path: '/' },
        { icon: 'Briefcase', label: 'Placements', path: '/placements' },
        { icon: 'Phone', label: 'AI Agent', path: '/agent' },
        { icon: 'Users', label: 'Accounts', path: '/accounts' },
        { icon: 'FileText', label: 'Call Records', path: '/calls' },
      ];
    }

    if (dashboardType === 'welcome_agent') {
      return [
        { icon: 'LayoutDashboard', label: 'Dashboard', path: '/' },
        { icon: 'Phone', label: 'Call Analytics', path: '/calls' },
        { icon: 'TrendingUp', label: 'FPD Analysis', path: '/fpd' },
        { icon: 'Building', label: 'Dealer Performance', path: '/dealers' },
      ];
    }

    return [];
  }, [dashboardType, isLoading]);
}

/**
 * Simple hook to get organization slug and name
 * Used for agent filtering in CallProcessing
 */
export function useOrgInfo(): { orgSlug: string | null; orgName: string | null } {
  const { organization, isLoaded } = useOrganization();
  
  if (!isLoaded || !organization) {
    return { orgSlug: null, orgName: null };
  }
  
  return {
    orgSlug: organization.slug || null,
    orgName: organization.name || null,
  };
}
